import { FC } from 'react';
import { cn } from '@/shared/lib';
import { TruncatedText } from '@/shared/ui';

/** Где стоит название элемента: слева от полосы либо под столбцом. */
export type ChartCategoryPlacement = 'left' | 'bottom';

type Props = {
  /** Ширина места под подпись у полосы; под столбцом место отмеряет само деление оси */
  width: number;
  /**
   * Точка деления оси: библиотека подставляет их сама. У полос `x` — правый край места под
   * подпись, от него она и отмеряется влево, поэтому у оси обнулены засечка и её отступ.
   */
  x?: number;
  y?: number;
  /** `offset` — половина ширины деления: по ней подпись под столбцом занимает ровно его место */
  payload?: { value?: string | number; offset?: number };
  placement?: ChartCategoryPlacement;
};

/** Высота места под одну подпись: в неё встаёт одна строка текста по середине своего деления. */
const TICK_HEIGHT = 24;

/**
 * Подпись деления оси названий: не поместившееся название усечено и раскрывается подсказкой.
 *
 * Подпись выведена разметкой внутри рисунка, а не его текстом: у текста рисунка нет ни усечения по
 * ширине, ни подсказки — обрезанное название осталось бы недоступным. Правило одно на оба
 * направления: и у полосы, и у столбца название усекается по отведённому ему месту.
 */
export const ChartCategoryTick: FC<Props> = ({ width, x = 0, y = 0, payload, placement = 'left' }) => {
  const label = String(payload?.value ?? '');
  const isBelow = placement === 'bottom';
  // Под столбцом место подписи — ширина его деления: она известна библиотеке, а заданная ширина
  // остаётся запасной на случай, если деление своей ширины не назвало.
  const boxWidth = isBelow && payload?.offset ? payload.offset * 2 : width;

  return (
    <foreignObject
      x={isBelow ? x - boxWidth / 2 : x - boxWidth}
      y={isBelow ? y : y - TICK_HEIGHT / 2}
      width={boxWidth}
      height={TICK_HEIGHT}
    >
      <div className={cn('flex h-full items-center', isBelow ? 'justify-center px-1' : 'justify-end pr-2')}>
        <TruncatedText className="max-w-full text-xs text-muted-foreground">{label}</TruncatedText>
      </div>
    </foreignObject>
  );
};
